/**
 * Session debugging utilities for troubleshooting authentication issues
 */

import { supabase } from '@/lib/supabase';

export interface SessionDebugInfo {
  hasClient: boolean;
  hasSession: boolean;
  sessionValid: boolean;
  sessionExpiry: string | null;
  timeUntilExpiry: number | null;
  userId: string | null;
  userEmail: string | null;
  storageKeys: string[];
  timestamp: string;
  errors: string[];
}

export const debugSession = async (): Promise<SessionDebugInfo> => {
  const errors: string[] = [];
  const timestamp = new Date().toISOString();
  
  try {
    // Check if client exists
    const hasClient = !!supabase;
    
    // Get session
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      errors.push(`Session error: ${error.message}`);
    }
    
    // Check localStorage keys
    const storageKeys = Object.keys(localStorage).filter(key => 
      key.includes('supabase') || key.includes('auth')
    );
    
    // Calculate session validity
    const hasSession = !!session;
    const sessionExpiry = session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null;
    const timeUntilExpiry = session?.expires_at ? 
      (new Date(session.expires_at * 1000).getTime() - Date.now()) : null;
    const sessionValid = hasSession && timeUntilExpiry ? timeUntilExpiry > 0 : false;
    
    return {
      hasClient,
      hasSession,
      sessionValid,
      sessionExpiry,
      timeUntilExpiry,
      userId: session?.user?.id || null,
      userEmail: session?.user?.email || null,
      storageKeys,
      timestamp,
      errors
    };
  } catch (error) {
    errors.push(`Debug error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    
    return {
      hasClient: false,
      hasSession: false,
      sessionValid: false,
      sessionExpiry: null,
      timeUntilExpiry: null,
      userId: null,
      userEmail: null,
      storageKeys: [],
      timestamp,
      errors
    };
  }
};

export const logSessionDebug = async () => {
  const debugInfo = await debugSession();
  
  if (!import.meta.env.PROD) {
    console.group('🔍 Session Debug Info');
    console.log('📅 Timestamp:', debugInfo.timestamp);
    console.log('🔧 Has Client:', debugInfo.hasClient);
    console.log('🎫 Has Session:', debugInfo.hasSession);
    console.log('✅ Session Valid:', debugInfo.sessionValid);
    console.log('⏰ Session Expiry:', debugInfo.sessionExpiry);
    console.log('⏳ Time Until Expiry (ms):', debugInfo.timeUntilExpiry);
    console.log('👤 User ID:', debugInfo.userId);
    console.log('📧 User Email:', debugInfo.userEmail);
    console.log('🗂️ Storage Keys:', debugInfo.storageKeys);
  }
  
  if (debugInfo.errors.length > 0) {
    console.error('❌ Errors:', debugInfo.errors);
  }
  
  if (!import.meta.env.PROD) console.groupEnd();
  
  return debugInfo;
};

export const clearSessionStorage = () => {
  if (!import.meta.env.PROD) console.log('🧹 Clearing session storage...');
  
  const keysToRemove = Object.keys(localStorage).filter(key => 
    key.includes('supabase') || key.includes('auth')
  );
  
  keysToRemove.forEach(key => {
    if (!import.meta.env.PROD) console.log(`🗑️ Removing storage key: ${key}`);
    localStorage.removeItem(key);
  });
  
  if (!import.meta.env.PROD) console.log(`✅ Cleared ${keysToRemove.length} storage keys`);
  return keysToRemove;
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).debugSession = logSessionDebug;
  (window as any).clearSessionStorage = clearSessionStorage;
}
